import "./style.scss";

import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { borderValue } from "components/ImportData";
import type { TImportTitles } from "components/ImportData/import_data";
import type { TObj } from "components/ImportData/type";
// import { findTitleStylesInfo } from "helpers/importData-helpers";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import OptimizedTableCell from "../OptimizedTableCell";
import StyledButton from "../StyledButton";
import StyledTextField from "../StyledTextField/StyledTextField";

type TProps = {
    importTitles: TImportTitles;
    loadedData: TObj[];
    setLoadedData: React.Dispatch<React.SetStateAction<TObj[]>>;
    templateKeys: string[];
    errors: Record<string, any>[];
    mandatoryFields?: string[];
};

let tableHeadCharCode = 65;

const RepairingTable = ({
    importTitles,
    loadedData,
    setLoadedData,
    templateKeys,
    errors,
    mandatoryFields = ["surname", "ean13", "price", "releaseDate", "amount", "packageQuantity", "beneficiaryId"],
}: TProps) => {
    const [t] = useTranslation();
    const [deleteCheck, setDeleteCheck] = useState<null | number>(null);

    // Large files only have a handful of error lines: index errors once and render only those rows,
    // otherwise every file row is mounted (hidden) and every cell scans the whole error list.
    const errorsByCell = useMemo(() => {
        const map = new Map<string, Record<string, any>>();
        errors.forEach((err) => {
            const cellKey = `${err.fileLine - 2}:${err.mappedKey}`;
            if (!map.has(cellKey)) map.set(cellKey, err);
        });
        return map;
    }, [errors]);
    const errorRowIndexes = useMemo(
        () =>
            Array.from(new Set(errors.map((err) => err.fileLine - 2)))
                .filter((index) => index >= 0 && index < loadedData.length)
                .sort((a, b) => a - b),
        [errors, loadedData.length],
    );

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number,
        title: string,
    ) => {
        const floatKeys = [
            "channelPercentage_1",
            "channelPercentage_2",
            "channelPercentage_3",
            "channelPercentage_4",
            "channelPercentage_5",
            "channelMilestone_1",
            "channelMilestone_2",
            "channelMilestone_3",
            "channelMilestone_4",
            "channelMilestone_5",
            "initialBalance",
            "initialProvision",
            "amount",
            "price",
        ];
        setLoadedData((prev) => {
            return prev.map((item, itemIndex) => {
                if (itemIndex === index) {
                    if (floatKeys.includes(title)) {
                        return { ...item, [title]: e.target.value.replaceAll(",", ".") };
                    } else {
                        return { ...item, [title]: e.target.value };
                    }
                } else {
                    return item;
                }
            });
        });
    };

    const handleDeleteLine = (index: number) => {
        setLoadedData((prev) => prev.map((e, i) => (i === index ? {} : e)));
        setDeleteCheck(null);
    };

    return (
        <Paper
            sx={{
                overflow: "hidden",
                border: borderValue,
                marginBottom: "0",
                borderRadius: "0",
                boxShadow: "none",
            }}
        >
            <TableContainer className="import-data-repairing-container">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    padding: "8px",
                                    width: "30px",
                                    height: "45px",
                                    borderRight: borderValue,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 10,
                                    background: "#F6F6F6",
                                }}
                            />
                            <TableCell
                                sx={{
                                    padding: "8px",
                                    width: "30px",
                                    height: "45px",
                                    borderRight: borderValue,
                                    position: "sticky",
                                    left: 73,
                                    zIndex: 10,
                                    background: "#F6F6F6",
                                }}
                            />
                            {Object.keys(importTitles).map((titleKey, index, array) => {
                                const titleValue = importTitles[titleKey];
                                tableHeadCharCode = tableHeadCharCode + 1;
                                if (index === array.length - 1) {
                                    const buf = tableHeadCharCode - 1;
                                    tableHeadCharCode = 65;
                                    return (
                                        <TableCell
                                            sx={{
                                                padding: "8px",
                                                borderBottom: "none",
                                                fontWeight: "600",
                                                textAlign: "center",
                                                borderRight: borderValue,
                                                background: "#F6F6F6",
                                            }}
                                            key={"importTitles" + index}
                                        >
                                            {String.fromCharCode(buf)}
                                        </TableCell>
                                    );
                                }
                                return (
                                    <TableCell
                                        sx={{
                                            padding: "8px",
                                            borderBottom: "none",
                                            fontWeight: "600",
                                            textAlign: "center",
                                            borderRight: borderValue,
                                            background: "#F6F6F6",
                                        }}
                                        key={"importTitles_index" + index}
                                    >
                                        {String.fromCharCode(tableHeadCharCode - 1)}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        <TableRow>
                            <TableCell
                                sx={{
                                    padding: "8px",
                                    width: "30px",
                                    height: "45px",
                                    borderRight: borderValue,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 10,
                                    background: "#F6F6F6",
                                }}
                            />
                            <TableCell
                                sx={{
                                    padding: "8px",
                                    width: "30px",
                                    height: "45px",
                                    textAlign: "center",
                                    fontWeight: "600",
                                    position: "sticky",
                                    left: 73,
                                    zIndex: 10,
                                    background: "#F6F6F6",
                                }}
                            >
                                1
                            </TableCell>
                            {Object.keys(importTitles).map((titleKey, index) => {
                                const titleValue = importTitles[titleKey];
                                return (
                                    <TableCell
                                        sx={{
                                            whiteSpace: "nowrap",
                                            border: "1px solid rgba(224, 224, 224, 1)",
                                            width: "fit-content",
                                            background: "#F6F6F6",
                                            textAlign: "center",
                                        }}
                                        key={"importTitles_title" + index}
                                    >
                                        {titleValue}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ maxHeight: "600px" }}>
                        {errorRowIndexes.map((index) => {
                            const item = loadedData[index];
                            if (!Object.keys(item).length) return null;
                            return (
                                <TableRow key={"loadedData" + index}>
                                    <TableCell
                                        sx={{
                                            textAlign: "center",
                                            borderRight: borderValue,
                                            fontWeight: "600",
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 10,
                                            background: "#F6F6F6",
                                            width: "30px",
                                        }}
                                    >
                                        {deleteCheck === index ? (
                                            <div>
                                                <StyledButton
                                                    className="import-data-repairing-remove-btn red"
                                                    sx={{ background: "#F6F6F6" }}
                                                    onClick={() => {
                                                        setDeleteCheck(null);
                                                    }}
                                                >
                                                    <Close />
                                                </StyledButton>
                                                <StyledButton
                                                    className="import-data-repairing-remove-btn green"
                                                    sx={{ background: "#F6F6F6" }}
                                                    onClick={() => {
                                                        handleDeleteLine(index);
                                                    }}
                                                >
                                                    <Check />
                                                </StyledButton>
                                            </div>
                                        ) : (
                                            <StyledButton
                                                className="import-data-repairing-remove-btn red"
                                                sx={{ background: "#F6F6F6" }}
                                                onClick={() => {
                                                    setDeleteCheck(index);
                                                }}
                                            >
                                                <DeleteIcon sx={{ backgroundColor: "#F6F6F6" }} />
                                            </StyledButton>
                                        )}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            textAlign: "center",
                                            borderRight: borderValue,
                                            fontWeight: "600",
                                            position: "sticky",
                                            left: 73,
                                            zIndex: 10,
                                            background: "#F6F6F6",
                                        }}
                                    >
                                        {index + 2}
                                    </TableCell>
                                    {Object.keys(importTitles).map((titleKey, indexKeys) => {
                                        const cellError = errorsByCell.get(`${index}:${titleKey}`);
                                        return !!cellError ||
                                            ((item[titleKey] === undefined || item[titleKey] === "") &&
                                                mandatoryFields.includes(titleKey)) ? (
                                            <TableCell sx={{ padding: 0 }} key={"templateKeys" + index + indexKeys}>
                                                <StyledTextField
                                                    onChange={(e) => handleInputChange(e, index, titleKey)}
                                                    sx={{
                                                        ".MuiOutlinedInput-root": {
                                                            backgroundColor: "rgba(197, 83, 83, 0.2)",
                                                        },
                                                    }}
                                                    id={`field-${indexKeys}`}
                                                    fullWidth
                                                    defaultValue={item[titleKey]}
                                                />
                                                <span style={{ fontSize: "10px" }}>
                                                    {cellError?.message ? t(cellError.message) : ""}
                                                </span>
                                            </TableCell>
                                        ) : (
                                            <OptimizedTableCell
                                                sx={{ textAlign: "center", borderRight: borderValue }}
                                                key={"templateKeys" + index + indexKeys}
                                            >
                                                {item[titleKey] as string}
                                            </OptimizedTableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default RepairingTable;
